// Load q2q.json: parse → check envelope → migrate older versions → validate (schema + structural rules).
// Anything newer, invalid or carrying unknown members is read-only and never repaired
// (docs/format/README.md §11). Runs in Workers, browsers and Node: validators are precompiled
// standalone code (scripts/build-validators.mjs), never eval'd at runtime.

import { cuesUntilNextStandby, DocIndex } from "./derive.ts";
import { FORMAT_VERSION, ID_RECORD_TYPES, type Anchor, type Doc } from "./types.ts";
import { v1, v2, v3, type Validator } from "./validators.gen.js";

const validateSchema = v3;
/** Schemas of older versions: a file is checked against its own version before it is migrated. */
const OLD_SCHEMAS: Record<number, Validator> = { 1: v1, 2: v2 };

/** Migrations from version N to N+1, keyed by N. Each gets a file valid in version N. */
const MIGRATIONS: Record<number, (raw: Record<string, unknown>) => Record<string, unknown>> = {
  // 1 → 2: a standby gains `through`, its last cue. In format 1 a standby covered every cue up to the
  // next standby, so `through` is the last of those, which keeps every standby's meaning.
  1: (raw) => {
    const v1 = { ...raw, standbys: (raw.standbys as { id: string; anchor: Anchor }[]).map((s) => ({ ...s, through: "" })) } as unknown as Doc;
    const ix = new DocIndex(v1);
    const standbys = v1.standbys.map((s) => ({ id: s.id, anchor: s.anchor, through: cuesUntilNextStandby(ix, s).at(-1)?.id ?? "" }));
    return { ...raw, formatVersion: 2, standbys };
  },
  // 2 → 3: absent placement continues to mean margin. Do not add defaults to existing records:
  // their canonical bytes and conditional-write hashes must stay unchanged.
  2: (raw) => ({ ...raw, formatVersion: 3 }),
};

export interface LoadResult {
  /** The document, when it could be read as the current format (possibly read-only). */
  doc: Doc | null;
  /** Empty when the file is fully valid and writable. */
  problems: string[];
  readOnly: boolean;
  /** Set when the file was migrated from an older version; its original bytes must be preserved before saving. */
  migratedFrom: number | null;
}

function schemaProblems(raw: unknown, validate: Validator = validateSchema): string[] {
  if (validate(raw)) return [];
  return (validate.errors ?? []).map((e) => {
    const where = e.instancePath || "/";
    if (e.keyword === "additionalProperties") return `${where}: unknown member "${String(e.params.additionalProperty)}"`;
    return `${where}: ${e.message ?? e.keyword}`;
  });
}

/** Rules JSON Schema can't express: unique ids, normalized rects, a complete page table. */
export function structuralProblems(doc: Doc): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const type of ID_RECORD_TYPES) {
    doc[type].forEach((r, i) => {
      if (seen.has(r.id)) problems.push(`/${type}/${i}/id: duplicate id "${r.id}"`);
      seen.add(r.id);
    });
  }
  const checkAnchor = (a: Anchor, path: string) => {
    if (a.shape !== "region") return;
    a.rects.forEach((r, i) => {
      if (!(r[0] < r[2] && r[1] < r[3])) problems.push(`${path}/rects/${i}: rect must have x0 < x1 and y0 < y1`);
    });
  };
  doc.moments.forEach((m, i) => checkAnchor(m.anchor, `/moments/${i}/anchor`));
  doc.standbys.forEach((s, i) => checkAnchor(s.anchor, `/standbys/${i}/anchor`));
  doc.notes.forEach((n, i) => checkAnchor(n.anchor, `/notes/${i}/anchor`));
  doc.pages.forEach((p, i) => {
    if (!(p.view[0] < p.view[2] && p.view[1] < p.view[3])) problems.push(`/pages/${i}/view: rect must have x0 < x1 and y0 < y1`);
  });
  const pageNumbers = doc.pages.map((p) => p.page).sort((a, b) => a - b);
  const count = doc.project.source.pageCount;
  if (pageNumbers.length !== count || pageNumbers.some((p, i) => p !== i + 1)) {
    problems.push(`/pages: must list pages 1 to ${count} exactly once each`);
  }
  return problems;
}

export function loadDoc(text: string): LoadResult {
  const fail = (problem: string): LoadResult => ({ doc: null, problems: [problem], readOnly: true, migratedFrom: null });
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    return fail(`not valid JSON: ${(e as Error).message}`);
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return fail("not a q2cue file: the top level isn't an object");
  let obj = raw as Record<string, unknown>;
  if (obj.format !== "q2q") return fail('not a q2cue file: "format" isn\'t "q2q"');
  const version = obj.formatVersion;
  if (typeof version !== "number" || !Number.isInteger(version) || version < 1) return fail('"formatVersion" must be a positive integer');

  if (version > FORMAT_VERSION) {
    // Never rewritten. Show it read-only if it happens to read as this version, so nothing is hidden.
    const problems = [`This project was saved by a newer q2cue (format ${version}); this q2cue reads format ${FORMAT_VERSION}. It is read-only.`];
    const asCurrent = { ...obj, formatVersion: FORMAT_VERSION };
    const readable = schemaProblems(asCurrent).length === 0 && structuralProblems(asCurrent as unknown as Doc).length === 0;
    return { doc: readable ? (asCurrent as unknown as Doc) : null, problems, readOnly: true, migratedFrom: null };
  }

  let migratedFrom: number | null = null;
  for (let v = version; v < FORMAT_VERSION; v++) {
    const step = MIGRATIONS[v];
    const check = OLD_SCHEMAS[v];
    if (!step || !check) return fail(`no migration from format ${v} to ${v + 1}`);
    // Only a valid file is migrated; an invalid one is reported against its own version.
    const old = schemaProblems(obj, check);
    if (old.length) return { doc: null, problems: old.map((p) => `${p} (format ${v})`), readOnly: true, migratedFrom: null };
    migratedFrom ??= version;
    obj = step(obj);
  }

  const problems = schemaProblems(obj);
  if (problems.length) {
    return { doc: null, problems, readOnly: true, migratedFrom };
  }
  // The schema has just proven the shape.
  const doc = obj as unknown as Doc;
  const structural = structuralProblems(doc);
  return { doc, problems: structural, readOnly: structural.length > 0, migratedFrom };
}
